import { PATHS } from './paths';
import {
  buildHousingSearchHref,
  buildRegionUnitList,
  FEDERAL_STATE_UNIT_CODE,
} from '@/common/helpers/buildHousingSearchHref';
import { buildDirectLinkHref } from '@/common/helpers/buildDirectLinkSlug';

const CATEGORY_CODE = {
  apartment: '27',
  house: '8',
  plot: '0',
  office: '35',
  commercial: '23',
} as const;

const CATEGORY_SLUG: Record<string, string> = {
  [CATEGORY_CODE.apartment]: 'wohnung',
  [CATEGORY_CODE.house]: 'haus',
  [CATEGORY_CODE.plot]: 'grundstueck',
  [CATEGORY_CODE.office]: 'buero',
  [CATEGORY_CODE.commercial]: 'gewerbe',
};

export type FooterLinkItem = {
  label: string;
  labelValues?: Record<string, string>;
  href: string;
};

export type FooterLinkSection = {
  title?: string;
  items: FooterLinkItem[];
};

export type FooterLinkColumn = {
  title?: string;
  items: FooterLinkItem[];
  sections?: FooterLinkSection[];
};

export type FooterLinkTab = {
  id: string;
  labelKey: string;
  columnCount?: number;
  columns: FooterLinkColumn[];
};

export type FooterLinkTabOption = {
  id: string;
  label: string;
};

const sortFooterLinkItems = (items: FooterLinkItem[]): FooterLinkItem[] =>
  [...items].sort((a, b) => a.label.localeCompare(b.label, 'de'));

const buildBrokerZipHref = (zip: number) => `/${PATHS.BROKERS}?zip=${zip}`;

const buildBrokerTownHref = (town: string) => `/${PATHS.BROKERS}?town=${encodeURIComponent(town)}`;

export const splitIntoColumns = (items: FooterLinkItem[], columnCount = 4): FooterLinkColumn[] => {
  const sorted = sortFooterLinkItems(items);
  const columns: FooterLinkColumn[] = Array.from({ length: columnCount }, () => ({ items: [] }));
  const itemsPerColumn = Math.ceil(sorted.length / columnCount);

  for (let col = 0; col < columnCount; col++) {
    columns[col].items = sorted.slice(col * itemsPerColumn, (col + 1) * itemsPerColumn);
  }

  return columns;
};

const splitIntoColumnsColumnWise = (
  items: FooterLinkItem[],
  columnCount: number,
): FooterLinkColumn[] => {
  const columns: FooterLinkColumn[] = Array.from({ length: columnCount }, () => ({ items: [] }));
  const baseSize = Math.floor(items.length / columnCount);
  const extra = items.length % columnCount;
  let index = 0;

  for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
    const size = baseSize + (columnIndex >= columnCount - extra ? 1 : 0);
    columns[columnIndex].items = items.slice(index, index + size);
    index += size;
  }

  return columns;
};

const splitSectionsIntoColumnsColumnWise = (
  sections: FooterLinkSection[],
  columnCount: number,
): FooterLinkColumn[] => {
  const columns: FooterLinkColumn[] = Array.from({ length: columnCount }, () => ({
    items: [],
    sections: [],
  }));
  const baseSize = Math.floor(sections.length / columnCount);
  const extra = sections.length % columnCount;
  let index = 0;

  for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
    const size = baseSize + (columnIndex >= columnCount - extra ? 1 : 0);
    columns[columnIndex].sections = sections.slice(index, index + size);
    index += size;
  }

  return columns;
};

const regionUnitList = (slugPart: keyof typeof FEDERAL_STATE_UNIT_CODE) =>
  buildRegionUnitList(FEDERAL_STATE_UNIT_CODE[slugPart]);

const REGION_I18N_KEY: Record<keyof typeof FEDERAL_STATE_UNIT_CODE, string> = {
  burgenland: 'burgenland',
  kaernten: 'carinthia',
  niederoesterreich: 'lowerAustria',
  oberoesterreich: 'upperAustria',
  salzburg: 'salzburg',
  steiermark: 'styria',
  tirol: 'tyrol',
  vorarlberg: 'vorarlberg',
  wien: 'vienna',
};

const regionLabelKey = (slugPart: keyof typeof FEDERAL_STATE_UNIT_CODE) =>
  `footerLinkBox.findBrokerTab.regions.${REGION_I18N_KEY[slugPart]}`;

const FEDERAL_STATES = [
  { label: regionLabelKey('burgenland'), slugPart: 'burgenland' as const },
  { label: regionLabelKey('kaernten'), slugPart: 'kaernten' as const },
  { label: regionLabelKey('niederoesterreich'), slugPart: 'niederoesterreich' as const },
  { label: regionLabelKey('oberoesterreich'), slugPart: 'oberoesterreich' as const },
  { label: regionLabelKey('salzburg'), slugPart: 'salzburg' as const },
  { label: regionLabelKey('steiermark'), slugPart: 'steiermark' as const },
  { label: regionLabelKey('tirol'), slugPart: 'tirol' as const },
  { label: regionLabelKey('vorarlberg'), slugPart: 'vorarlberg' as const },
  { label: regionLabelKey('wien'), slugPart: 'wien' as const },
];

const STATE_CAPITALS = [
  { label: 'footerLinkBox.findBrokerTab.places.bregenz', slugPart: 'bregenz' },
  { label: 'footerLinkBox.findBrokerTab.places.eisenstadt', slugPart: 'eisenstadt' },
  { label: 'footerLinkBox.findBrokerTab.places.graz', slugPart: 'graz' },
  { label: 'footerLinkBox.findBrokerTab.places.innsbruck', slugPart: 'innsbruck' },
  { label: 'footerLinkBox.findBrokerTab.places.klagenfurt', slugPart: 'klagenfurt' },
  { label: 'footerLinkBox.findBrokerTab.places.linz', slugPart: 'linz' },
  { label: 'footerLinkBox.findBrokerTab.places.salzburg', slugPart: 'salzburg' },
  { label: 'footerLinkBox.findBrokerTab.places.sanktPoelten', slugPart: 'st-poelten' },
] as const;

const VIENNA_DISTRICT_SLUGS = [
  'innere-stadt',
  'leopoldstadt',
  'landstrasse',
  'wieden',
  'margareten',
  'mariahilf',
  'neubau',
  'josefstadt',
  'alsergrund',
  'favoriten',
  'simmering',
  'meidling',
  'hietzing',
  'penzing',
  'rh-fh',
  'ottakring',
  'hernals',
  'waehring',
  'doebling',
  'brigittenau',
  'floridsdorf',
  'donaustadt',
  'liesing',
] as const;

const DISTRICT_I18N_KEY: Record<(typeof VIENNA_DISTRICT_SLUGS)[number], string> = {
  'innere-stadt': 'innerCity',
  leopoldstadt: 'leopoldstadt',
  landstrasse: 'landstrasse',
  wieden: 'wieden',
  margareten: 'margareten',
  mariahilf: 'mariahilf',
  neubau: 'neubau',
  josefstadt: 'josefstadt',
  alsergrund: 'alsergrund',
  favoriten: 'favoriten',
  simmering: 'simmering',
  meidling: 'meidling',
  hietzing: 'hietzing',
  penzing: 'penzing',
  'rh-fh': 'rudolfsheimFuenfhaus',
  ottakring: 'ottakring',
  hernals: 'hernals',
  waehring: 'waehring',
  doebling: 'doebling',
  brigittenau: 'brigittenau',
  floridsdorf: 'floridsdorf',
  donaustadt: 'donaustadt',
  liesing: 'liesing',
};

const VIENNA_DISTRICTS = VIENNA_DISTRICT_SLUGS.map((slug, index) => ({
  nr: index + 1,
  slug,
}));

const PROPERTY_TYPE_CODES = [
  CATEGORY_CODE.apartment,
  CATEGORY_CODE.house,
  CATEGORY_CODE.plot,
  CATEGORY_CODE.office,
  CATEGORY_CODE.commercial,
];

type UniqueFeature =
  | { label: string; equipment: { code: string; title: string } }
  | { label: string; hwbClass: string[] };

const FEATURE_ITEMS: UniqueFeature[] = [
  {
    label: 'footerLinkBox.features.barrierFree',
    equipment: { code: 'disabledFriendly.true', title: 'Barrierefrei' },
  },
  {
    label: 'footerLinkBox.features.balconyLoggia',
    equipment: { code: 'hasBalcony.true', title: 'Balkon / Loggia' },
  },
  {
    label: 'footerLinkBox.features.terrace',
    equipment: { code: 'hasTerrace.true', title: 'Terrasse' },
  },
  {
    label: 'footerLinkBox.features.garden',
    equipment: { code: 'hasGarden.true', title: 'Garten' },
  },
  {
    label: 'footerLinkBox.features.lowHeatingDemand',
    hwbClass: ['A++', 'A+', 'A', 'B'],
  },
];

const RECENT_SEARCH_LINKS: Array<{ label: string; slug: string }> = [
  { label: 'footerLinkBox.recentSearches.buyApartmentInTulln', slug: 'wohnung-kaufen-in-tulln' },
  {
    label: 'footerLinkBox.recentSearches.apartmentsForSaleInVienna',
    slug: 'wohnung-kaufen-in-wien',
  },
  {
    label: 'footerLinkBox.recentSearches.apartmentsForRentInVienna',
    slug: 'wohnung-mieten-in-wien',
  },
  { label: 'footerLinkBox.recentSearches.buyHouseInVienna', slug: 'haus-kaufen-in-wien' },
  {
    label: 'footerLinkBox.recentSearches.apartmentsForSaleInGraz',
    slug: 'wohnung-kaufen-in-graz',
  },
  { label: 'footerLinkBox.recentSearches.apartmentsForRentInGraz', slug: 'wohnung-mieten-in-graz' },
  { label: 'footerLinkBox.recentSearches.buyHouseInGraz', slug: 'haus-kaufen-in-graz' },
  { label: 'footerLinkBox.recentSearches.buyHouseInStyria', slug: 'haus-kaufen-in-steiermark' },
  {
    label: 'footerLinkBox.recentSearches.buyPlotInStyria',
    slug: 'grundstueck-kaufen-in-steiermark',
  },
  {
    label: 'footerLinkBox.recentSearches.apartmentsForSaleInSalzburg',
    slug: 'wohnung-kaufen-in-salzburg',
  },
  {
    label: 'footerLinkBox.recentSearches.apartmentsForRentInSalzburg',
    slug: 'wohnung-mieten-in-salzburg',
  },
  { label: 'footerLinkBox.recentSearches.buyHouseInSalzburg', slug: 'haus-kaufen-in-salzburg' },
  {
    label: 'footerLinkBox.recentSearches.apartmentsForSaleInInnsbruck',
    slug: 'wohnung-kaufen-in-innsbruck',
  },
  {
    label: 'footerLinkBox.recentSearches.apartmentsForRentInInnsbruck',
    slug: 'wohnung-mieten-in-innsbruck',
  },
  { label: 'footerLinkBox.recentSearches.buyHouseInInnsbruck', slug: 'haus-kaufen-in-innsbruck' },
  { label: 'footerLinkBox.recentSearches.buyHouseInTyrol', slug: 'haus-kaufen-in-tirol' },
  { label: 'footerLinkBox.recentSearches.buyPlotInTyrol', slug: 'grundstueck-kaufen-in-tirol' },
  {
    label: 'footerLinkBox.recentSearches.apartmentsForSaleInLinz',
    slug: 'wohnung-kaufen-in-linz',
  },
  { label: 'footerLinkBox.recentSearches.apartmentsForRentInLinz', slug: 'wohnung-mieten-in-linz' },
  { label: 'footerLinkBox.recentSearches.buyHouseInLinz', slug: 'haus-kaufen-in-linz' },
  {
    label: 'footerLinkBox.recentSearches.buyHouseInUpperAustria',
    slug: 'haus-kaufen-in-oberoesterreich',
  },
  {
    label: 'footerLinkBox.recentSearches.buyPlotInUpperAustria',
    slug: 'grundstueck-kaufen-in-oberoesterreich',
  },
  {
    label: 'footerLinkBox.recentSearches.buyHouseInLowerAustria',
    slug: 'haus-kaufen-in-niederoesterreich',
  },
  {
    label: 'footerLinkBox.recentSearches.buyPlotInLowerAustria',
    slug: 'grundstueck-kaufen-in-niederoesterreich',
  },
  { label: 'footerLinkBox.recentSearches.buyHouseInCarinthia', slug: 'haus-kaufen-in-kaernten' },
  {
    label: 'footerLinkBox.recentSearches.buyPlotInCarinthia',
    slug: 'grundstueck-kaufen-in-kaernten',
  },
  {
    label: 'footerLinkBox.recentSearches.buyHouseInVorarlberg',
    slug: 'haus-kaufen-in-vorarlberg',
  },
  {
    label: 'footerLinkBox.recentSearches.buyPlotInVorarlberg',
    slug: 'grundstueck-kaufen-in-vorarlberg',
  },
  {
    label: 'footerLinkBox.recentSearches.buyHouseInBurgenland',
    slug: 'haus-kaufen-in-burgenland',
  },
  {
    label: 'footerLinkBox.recentSearches.buyPlotInBurgenland',
    slug: 'grundstueck-kaufen-in-burgenland',
  },
];

const buildRecentSearchItems = (): FooterLinkItem[] =>
  RECENT_SEARCH_LINKS.map(({ label, slug }) => ({
    label,
    href: buildDirectLinkHref(slug),
  }));

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  [CATEGORY_CODE.apartment]: 'footerLinkBox.categories.apartment',
  [CATEGORY_CODE.house]: 'footerLinkBox.categories.house',
  [CATEGORY_CODE.plot]: 'footerLinkBox.categories.plot',
  [CATEGORY_CODE.office]: 'footerLinkBox.categories.office',
  [CATEGORY_CODE.commercial]: 'footerLinkBox.categories.commercial',
};

const inRegionKey = (slugPart: keyof typeof FEDERAL_STATE_UNIT_CODE) =>
  `footerLinkBox.inRegion.${REGION_I18N_KEY[slugPart]}`;

const districtMessageKey = (slug: (typeof VIENNA_DISTRICT_SLUGS)[number]) =>
  DISTRICT_I18N_KEY[slug];

const buildRegionsColumns = (): FooterLinkColumn[] => {
  const federalStateItems: FooterLinkItem[] = FEDERAL_STATES.map(({ label, slugPart }) => ({
    label,
    href: buildDirectLinkHref(`haus-kaufen-in-${slugPart}`),
  }));

  const viennaDistrictItems: FooterLinkItem[] = VIENNA_DISTRICTS.map(({ nr, slug }) => ({
    label: `footerLinkBox.viennaDistrictLinks.${districtMessageKey(slug)}`,
    href: buildDirectLinkHref(`wohnung-kaufen-in-wien-${nr}-${slug}`),
  }));

  const stateCapitalItems: FooterLinkItem[] = STATE_CAPITALS.map(({ label, slugPart }) => ({
    label,
    href: buildDirectLinkHref(`wohnung-kaufen-in-${slugPart}`),
  }));

  const viennaColumns = splitIntoColumnsColumnWise(viennaDistrictItems, 2);

  return [
    { title: 'footerLinkBox.federalStates', items: federalStateItems },
    { title: 'footerLinkBox.viennaDistricts', items: viennaColumns[0].items },
    { title: '', items: viennaColumns[1].items },
    { title: 'footerLinkBox.stateCapitals', items: stateCapitalItems },
  ];
};

const buildPopularPropertyTypeItem = (
  code: string,
  slugPart: keyof typeof FEDERAL_STATE_UNIT_CODE,
): FooterLinkItem => ({
  label: 'footerLinkBox.popularPropertyTypeItem',
  labelValues: {
    category: CATEGORY_LABEL_KEYS[code],
    inRegion: inRegionKey(slugPart),
  },
  href: buildDirectLinkHref(`${CATEGORY_SLUG[code]}-kaufen-in-${slugPart}`),
});

const REGION_SECTION_COLUMN_COUNT = 3;

const buildPopularPropertyTypeColumns = (): FooterLinkColumn[] => {
  const regionSections: FooterLinkSection[] = FEDERAL_STATES.map(({ label, slugPart }) => ({
    title: label,
    items: PROPERTY_TYPE_CODES.map((code) => buildPopularPropertyTypeItem(code, slugPart)),
  }));

  return splitSectionsIntoColumnsColumnWise(regionSections, REGION_SECTION_COLUMN_COUNT);
};

const buildUniqueFeatureItem = (
  feature: UniqueFeature,
  slugPart: keyof typeof FEDERAL_STATE_UNIT_CODE,
): FooterLinkItem => ({
  label: 'footerLinkBox.uniquePropertyItem',
  labelValues: {
    feature: feature.label,
    inRegion: inRegionKey(slugPart),
  },
  href: buildHousingSearchHref({
    unitList: regionUnitList(slugPart),
    ...('equipment' in feature
      ? { equipmentValue: [feature.equipment] }
      : { hwbClass: feature.hwbClass }),
  }),
});

const buildUniquePropertyColumns = (): FooterLinkColumn[] => {
  const regionSections: FooterLinkSection[] = FEDERAL_STATES.map(({ label, slugPart }) => ({
    title: label,
    items: FEATURE_ITEMS.map((feature) => buildUniqueFeatureItem(feature, slugPart)),
  }));

  return splitSectionsIntoColumnsColumnWise(regionSections, REGION_SECTION_COLUMN_COUNT);
};

const findBrokerDistrictKey = (slug: (typeof VIENNA_DISTRICT_SLUGS)[number]) =>
  `footerLinkBox.findBrokerTab.districts.${districtMessageKey(slug)}`;

const FIND_BROKER_SECTIONS: Array<{ title: string; items: FooterLinkItem[] }> = [
  {
    title: 'footerLinkBox.findBrokerTab.regions.vienna',
    items: VIENNA_DISTRICTS.map(({ nr, slug }) => ({
      label: findBrokerDistrictKey(slug),
      href: buildBrokerZipHref(1000 + nr * 10),
    })),
  },
  {
    title: 'footerLinkBox.findBrokerTab.regions.styria',
    items: [
      { label: 'footerLinkBox.findBrokerTab.places.graz', href: buildBrokerTownHref('graz') },
      { label: 'footerLinkBox.findBrokerTab.places.leoben', href: buildBrokerTownHref('leoben') },
    ],
  },
  {
    title: 'footerLinkBox.findBrokerTab.regions.upperAustria',
    items: [
      { label: 'footerLinkBox.findBrokerTab.places.linz', href: buildBrokerTownHref('linz') },
      { label: 'footerLinkBox.findBrokerTab.places.wels', href: buildBrokerTownHref('wels') },
      { label: 'footerLinkBox.findBrokerTab.places.steyr', href: buildBrokerTownHref('steyr') },
    ],
  },
  {
    title: 'footerLinkBox.findBrokerTab.regions.salzburg',
    items: [
      {
        label: 'footerLinkBox.findBrokerTab.places.salzburgCity',
        href: buildBrokerTownHref('salzburg'),
      },
    ],
  },
  {
    title: 'footerLinkBox.findBrokerTab.regions.tyrol',
    items: [
      {
        label: 'footerLinkBox.findBrokerTab.places.innsbruck',
        href: buildBrokerTownHref('innsbruck'),
      },
      {
        label: 'footerLinkBox.findBrokerTab.places.kitzbuhel',
        href: buildBrokerTownHref('kitzbühel'),
      },
    ],
  },
  {
    title: 'footerLinkBox.findBrokerTab.regions.carinthia',
    items: [
      {
        label: 'footerLinkBox.findBrokerTab.places.klagenfurtOnWoerthersee',
        href: buildBrokerTownHref('klagenfurt am wörthersee'),
      },
      { label: 'footerLinkBox.findBrokerTab.places.villach', href: buildBrokerTownHref('villach') },
      {
        label: 'footerLinkBox.findBrokerTab.places.wolfsberg',
        href: buildBrokerTownHref('wolfsberg'),
      },
    ],
  },
  {
    title: 'footerLinkBox.findBrokerTab.regions.lowerAustria',
    items: [
      {
        label: 'footerLinkBox.findBrokerTab.places.sanktPoelten',
        href: buildBrokerTownHref('sankt pölten'),
      },
      {
        label: 'footerLinkBox.findBrokerTab.places.wienerNeustadt',
        href: buildBrokerTownHref('wiener neustadt'),
      },
      { label: 'footerLinkBox.findBrokerTab.places.baden', href: buildBrokerTownHref('baden') },
    ],
  },
  {
    title: 'footerLinkBox.findBrokerTab.regions.vorarlberg',
    items: [
      {
        label: 'footerLinkBox.findBrokerTab.places.dornbirn',
        href: buildBrokerTownHref('dornbirn'),
      },
      {
        label: 'footerLinkBox.findBrokerTab.places.feldkirch',
        href: buildBrokerTownHref('feldkirch'),
      },
      { label: 'footerLinkBox.findBrokerTab.places.bregenz', href: buildBrokerTownHref('bregenz') },
    ],
  },
  {
    title: 'footerLinkBox.findBrokerTab.regions.burgenland',
    items: [
      {
        label: 'footerLinkBox.findBrokerTab.places.eisenstadt',
        href: buildBrokerTownHref('eisenstadt'),
      },
    ],
  },
];
const buildFindBrokerColumns = (): FooterLinkColumn[] => {
  const taggedItems = FIND_BROKER_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({ ...item, section: section.title })),
  );

  const columnChunks = splitIntoColumnsColumnWise(
    taggedItems.map(({ label, href }) => ({ label, href })),
    4,
  );

  let itemOffset = 0;

  return columnChunks.map((column) => {
    const startIndex = itemOffset;
    const endIndex = startIndex + column.items.length;
    const columnTagged = taggedItems.slice(startIndex, endIndex);
    const previousItem = startIndex > 0 ? taggedItems[startIndex - 1] : undefined;
    const title =
      columnTagged[0] && (!previousItem || previousItem.section !== columnTagged[0].section)
        ? columnTagged[0].section
        : undefined;

    itemOffset = endIndex;

    return { title, items: column.items };
  });
};

export const FOOTER_LINK_TABS: FooterLinkTab[] = [
  {
    id: 'from-the-regions',
    labelKey: 'footerLinkBox.fromTheRegions',
    columns: buildRegionsColumns(),
  },
  {
    id: 'popular-property-types',
    labelKey: 'footerLinkBox.popularPropertyTypes',
    columnCount: REGION_SECTION_COLUMN_COUNT,
    columns: buildPopularPropertyTypeColumns(),
  },
  {
    id: 'unique-properties',
    labelKey: 'footerLinkBox.uniqueProperties',
    columnCount: REGION_SECTION_COLUMN_COUNT,
    columns: buildUniquePropertyColumns(),
  },
  {
    id: 'recent-search-queries',
    labelKey: 'footerLinkBox.recentSearchQueries',
    columns: splitIntoColumnsColumnWise(buildRecentSearchItems(), 4),
  },
  {
    id: 'find-a-broker',
    labelKey: 'footerLinkBox.findABroker',
    columns: buildFindBrokerColumns(),
  },
];

export const FOOTER_LINK_TAB_OPTIONS: FooterLinkTabOption[] = FOOTER_LINK_TABS.map(
  ({ id, labelKey }) => ({
    id,
    label: labelKey,
  }),
);
